import {
  createAuthenticatedLootRateDisplayPreferenceRepository,
  decodeLootRateDisplayModeV1,
  decodeLootRateDisplayPreferenceProjectionV1,
} from "./account/loot-rate-display-preference-repository.js";
import {
  browserAccountStateSessionRepository,
} from "./account/account-state-session-repository.js";
import {
  createBrowserLootRateDisplayPreferenceRepository,
  LOOT_RATE_DISPLAY_PREFERENCE_SCHEMA_VERSION,
  lootRateDisplayPreferenceDecision,
} from "./loot-rate-display-preference.js";

const REPOSITORY_BRAND = Symbol.for(
  "fishystuff.loot-rate-display-preference-repository.v1",
);

function hasMethods(value, names) {
  return names.every((name) => typeof value?.[name] === "function");
}

function statesEqual(left, right) {
  return JSON.stringify(left) === JSON.stringify(right);
}

function freezeState(value) {
  const next = { ...value };
  if (next.authoritative) {
    next.authoritative = freezeState(next.authoritative);
  }
  if (next.legacyCleanup) {
    next.legacyCleanup = Object.freeze({ ...next.legacyCleanup });
  }
  return Object.freeze(next);
}

function projectionState(value) {
  const projection = decodeLootRateDisplayPreferenceProjectionV1(value);
  return projection.kind === "missing"
    ? freezeState({ kind: "missing" })
    : freezeState({
      kind: "ready",
      revision: projection.revision,
      mode: projection.mode,
    });
}

function boundaryState(remoteState) {
  if (
    remoteState?.kind === "uninitialized"
    || remoteState?.kind === "loading"
  ) {
    return freezeState({ kind: remoteState.kind });
  }
  if (remoteState?.kind === "locked") {
    return freezeState({
      kind: "locked",
      reason: remoteState.reason || "account-locked",
    });
  }
  if (remoteState?.kind === "unavailable") {
    return freezeState({
      kind: "unavailable",
      operation: remoteState.operation || "account",
      reason: remoteState.reason || "account-state-unavailable",
    });
  }
  return freezeState({
    kind: "unavailable",
    operation: "account",
    reason: "account-loot-rate-display-state-invalid",
  });
}

function pendingState(remoteState) {
  return freezeState({
    kind: "pending",
    operation: remoteState.operation,
    authoritative: projectionState(remoteState.authoritativeState),
  });
}

function conflictState(remoteState) {
  const authoritative = projectionState(remoteState.authoritativeState);
  return freezeState({
    kind: "conflict",
    operation: remoteState.operation,
    expectedRevision: remoteState.expectedRevision,
    actualRevision: authoritative.kind === "ready"
      ? authoritative.revision
      : null,
    authoritative,
  });
}

function requireLocalRepository(repository) {
  if (
    !hasMethods(repository, [
      "decision",
      "dispose",
      "exportRecord",
      "load",
      "reset",
      "setMode",
      "state",
      "subscribe",
    ])
  ) {
    throw new TypeError(
      "Loot-rate display preferences require the exact local repository contract.",
    );
  }
  return repository;
}

export function createAccountAwareLootRateDisplayPreferenceRepository({
  localRepositoryFactory,
  authenticatedRepository,
} = {}) {
  if (typeof localRepositoryFactory !== "function") {
    throw new TypeError(
      "Loot-rate display preferences require a local repository factory.",
    );
  }
  if (
    !hasMethods(authenticatedRepository, [
      "dispose",
      "load",
      "reset",
      "setMode",
      "state",
      "subscribe",
    ])
  ) {
    throw new TypeError(
      "Loot-rate display preferences require the authenticated repository contract.",
    );
  }

  let currentState = freezeState({ kind: "uninitialized" });
  let authority = "unknown";
  let localRepository = null;
  let unsubscribeLocal = null;
  let disposed = false;
  const listeners = new Set();

  function state() {
    return currentState;
  }

  function decision() {
    return lootRateDisplayPreferenceDecision(currentState);
  }

  function deliver(listener) {
    try {
      listener(currentState);
    } catch (_error) {
      globalThis.reportError?.(
        new Error("Loot-rate display preference subscriber failed."),
      );
    }
  }

  function publish(nextState) {
    if (disposed) return currentState;
    const frozen = freezeState(nextState);
    if (statesEqual(currentState, frozen)) return currentState;
    currentState = frozen;
    for (const listener of listeners) deliver(listener);
    return currentState;
  }

  function releaseLocal() {
    if (!localRepository) return;
    const repository = localRepository;
    authority = "unknown";
    localRepository = null;
    unsubscribeLocal?.();
    unsubscribeLocal = null;
    repository.dispose();
  }

  function createLocalRepository() {
    return requireLocalRepository(localRepositoryFactory());
  }

  function activateLocal() {
    if (disposed || (authority === "local" && localRepository)) return state();
    releaseLocal();
    let repository = null;
    try {
      repository = createLocalRepository();
      const loaded = repository.load();
      authority = "local";
      localRepository = repository;
      unsubscribeLocal = repository.subscribe((nextState) => {
        if (
          !disposed
          && authority === "local"
          && repository === localRepository
        ) {
          publish(nextState);
        }
      });
      return publish(loaded ?? repository.state());
    } catch (_error) {
      repository?.dispose?.();
      authority = "unknown";
      localRepository = null;
      unsubscribeLocal = null;
      return publish({
        kind: "unavailable",
        operation: "load",
        reason: "local-loot-rate-display-load-failed",
      });
    }
  }

  function projectAuthenticated(remoteState) {
    if (disposed) return;
    if (remoteState?.kind === "signedOut") {
      activateLocal();
      return;
    }

    releaseLocal();
    try {
      if (remoteState?.kind === "ready") {
        authority = "account";
        publish(projectionState(remoteState.authoritativeState));
        return;
      }
      if (remoteState?.kind === "pending") {
        authority = "account";
        publish(pendingState(remoteState));
        return;
      }
      if (remoteState?.kind === "conflict") {
        authority = "account";
        publish(conflictState(remoteState));
        return;
      }
      authority = "unknown";
      publish(boundaryState(remoteState));
    } catch (_error) {
      authority = "unknown";
      publish({
        kind: "unavailable",
        operation: "load",
        reason: "account-loot-rate-display-state-invalid",
      });
    }
  }

  const unsubscribeAuthenticated = authenticatedRepository.subscribe(
    projectAuthenticated,
  );

  async function load() {
    if (disposed) return state();
    await authenticatedRepository.load();
    return state();
  }

  async function setMode(mode, options) {
    const exactMode = decodeLootRateDisplayModeV1(mode);
    if (disposed || currentState.kind === "pending") return state();
    if (authority === "local" && localRepository) {
      await localRepository.setMode(exactMode, options);
    } else if (authority === "account") {
      await authenticatedRepository.setMode(exactMode, options);
    }
    return state();
  }

  async function reset(options) {
    if (disposed || currentState.kind === "pending") return state();
    if (authority === "local" && localRepository) {
      await localRepository.reset(options);
    } else if (authority === "account") {
      await authenticatedRepository.reset(options);
    }
    return state();
  }

  async function resetLocal(options) {
    if (disposed) {
      return freezeState({
        kind: "unavailable",
        operation: "reset",
        reason: "loot-rate-display-repository-disposed",
      });
    }
    if (authority === "local" && localRepository) {
      return localRepository.reset(options);
    }

    const temporary = createLocalRepository();
    try {
      return await temporary.reset(options);
    } finally {
      temporary.dispose();
    }
  }

  function subscribe(listener) {
    if (typeof listener !== "function") {
      throw new TypeError(
        "Loot-rate display preference listener must be a function.",
      );
    }
    if (disposed) return () => {};
    listeners.add(listener);
    deliver(listener);
    return () => listeners.delete(listener);
  }

  function dispose() {
    if (disposed) return;
    disposed = true;
    unsubscribeAuthenticated();
    releaseLocal();
    listeners.clear();
    authenticatedRepository.dispose();
  }

  return Object.freeze({
    [REPOSITORY_BRAND]: LOOT_RATE_DISPLAY_PREFERENCE_SCHEMA_VERSION,
    decision,
    dispose,
    load,
    reset,
    resetLocal,
    setMode,
    state,
    subscribe,
  });
}

let browserOwner = null;
let browserRepository = null;
let browserWindow = null;

function browserWindowRef() {
  const windowRef = globalThis.window;
  if (!windowRef || typeof windowRef !== "object") {
    throw new TypeError(
      "Browser loot-rate display preferences require a window.",
    );
  }
  return windowRef;
}

function disposeBrowserOwner() {
  browserOwner?.dispose();
  browserOwner = null;
  browserRepository = null;
  browserWindow = null;
}

export function browserLootRateDisplayPreferenceRepository() {
  const windowRef = browserWindowRef();
  if (browserOwner && browserWindow !== windowRef) {
    disposeBrowserOwner();
  }
  if (!browserOwner) {
    const authenticatedRepository =
      createAuthenticatedLootRateDisplayPreferenceRepository({
        sessionRepository: browserAccountStateSessionRepository(),
        apiBaseUrl: windowRef.__fishystuffApiBaseUrl,
      });
    browserOwner = createAccountAwareLootRateDisplayPreferenceRepository({
      localRepositoryFactory:
        createBrowserLootRateDisplayPreferenceRepository,
      authenticatedRepository,
    });
    const owner = browserOwner;
    browserRepository = Object.freeze({
      [REPOSITORY_BRAND]: LOOT_RATE_DISPLAY_PREFERENCE_SCHEMA_VERSION,
      decision: browserOwner.decision,
      dispose() {
        if (browserOwner === owner) disposeBrowserOwner();
        else owner.dispose();
      },
      load: browserOwner.load,
      reset: browserOwner.reset,
      resetLocal: browserOwner.resetLocal,
      setMode: browserOwner.setMode,
      state: browserOwner.state,
      subscribe: browserOwner.subscribe,
    });
    browserWindow = windowRef;
    void browserOwner.load();
  }
  return browserRepository;
}
